import React, { useEffect, useState } from 'react';
import './Comments.css';

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

function ReplyForm({ user, parentId, onPost, onCancel }) {
  const [text,    setText]    = useState('');
  const [posting, setPosting] = useState(false);
  const [error,   setError]   = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    setError('');
    try {
      const res  = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId:     user.id,
          userName:   user.name,
          userAvatar: user.avatar || user.name?.[0]?.toUpperCase() || '?',
          content:    text.trim(),
          parentId,
        }),
      });
      const json = await res.json();
      if (json.success) { onPost(json.data); setText(''); }
      else setError('Your reply could not be posted. Please try again.');
    } catch {
      setError('Your reply could not be posted. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <form className="reply-form" onSubmit={submit}>
      <div className="comment-form-avatar reply-form-avatar" aria-hidden="true">
        {user.avatar || user.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="comment-form-right">
        <label htmlFor={`reply-input-${parentId}`} className="sr-only">Reply to this comment</label>
        <textarea
          id={`reply-input-${parentId}`}
          className="comment-input"
          placeholder="Write your reply"
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
          maxLength={1000}
          autoFocus
        />
        <div className="comment-form-actions">
          <span className="comment-status" role="status">{posting ? 'Posting your reply' : ''}</span>
          <button type="button" className="btn btn-ghost reply-cancel" onClick={onCancel}>Cancel reply</button>
          <button type="submit" className="btn btn-primary comment-submit" disabled={!text.trim() || posting}>
            {posting ? 'Posting…' : 'Post reply'}
          </button>
        </div>
        {error && <p className="comment-error" role="alert">{error}</p>}
      </div>
    </form>
  );
}

function CommentItem({ comment, replies, user, onDelete, onReply }) {
  const [showReply, setShowReply] = useState(false);
  const canDelete = user.role === 'admin' || comment.user_id === user.id;

  const handlePost = (newReply) => {
    onReply(newReply);
    setShowReply(false);
  };

  return (
    <div className="comment-item">
      <div className="comment-avatar" aria-hidden="true">
        {comment.user_avatar || comment.user_name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="comment-body">
        <div className="comment-meta">
          <span className="comment-name">{comment.user_name}</span>
          <span className="comment-time">{timeAgo(comment.created_at)}</span>
        </div>
        <p className="comment-text">{comment.content}</p>
        <div className="comment-actions">
          <button type="button" className="comment-reply-btn" aria-expanded={showReply} onClick={() => setShowReply(r => !r)}>
            Reply<span className="sr-only"> to {comment.user_name}</span>{replies.length > 0 ? ` (${replies.length} ${replies.length === 1 ? 'reply' : 'replies'})` : ''}
          </button>
          {canDelete && (
            <button type="button" className="comment-delete-inline" onClick={() => onDelete(comment.id)}>Delete<span className="sr-only"> comment by {comment.user_name}</span></button>
          )}
        </div>

        {showReply && (
          <ReplyForm
            user={user}
            parentId={comment.id}
            onPost={handlePost}
            onCancel={() => setShowReply(false)}
          />
        )}

        {replies.length > 0 && (
          <div className="comment-replies">
            {replies.map(r => (
              <div key={r.id} className="comment-item comment-reply">
                <div className="comment-avatar comment-avatar--sm" aria-hidden="true">
                  {r.user_avatar || r.user_name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="comment-body">
                  <div className="comment-meta">
                    <span className="comment-name">{r.user_name}</span>
                    <span className="comment-time">{timeAgo(r.created_at)}</span>
                  </div>
                  <p className="comment-text">{r.content}</p>
                  {(user.role === 'admin' || r.user_id === user.id) && (
                    <div className="comment-actions">
                      <button type="button" className="comment-delete-inline" onClick={() => onDelete(r.id)}>Delete<span className="sr-only"> reply by {r.user_name}</span></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Comments({ user }) {
  const [comments, setComments] = useState([]);
  const [text,     setText]     = useState('');
  const [posting,  setPosting]  = useState(false);
  const [notice,   setNotice]   = useState('');
  const [failed,   setFailed]   = useState(false);

  const load = () => {
    fetch('/api/comments')
      .then(r => r.json())
      .then(j => { if (j.success) setComments(j.data); })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    setNotice('');
    setFailed(false);
    try {
      const res  = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId:     user.id,
          userName:   user.name,
          userAvatar: user.avatar || user.name?.[0]?.toUpperCase() || '?',
          content:    text.trim(),
        }),
      });
      const json = await res.json();
      if (json.success) { setComments(prev => [...prev, json.data]); setText(''); setNotice('Comment posted.'); }
      else { setFailed(true); setNotice('Your comment could not be posted. Please try again.'); }
    } catch {
      setFailed(true);
      setNotice('Your comment could not be posted. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    await fetch(`/api/comments/${id}`, { method: 'DELETE' });
    setComments(prev => prev.filter(c => c.id !== id && c.parent_id !== id));
    setFailed(false);
    setNotice('Comment deleted.');
  };

  const addReply = (reply) => setComments(prev => [...prev, reply]);

  // Separate top-level from replies
  const topLevel = comments.filter(c => !c.parent_id);
  const repliesFor = (id) => comments.filter(c => c.parent_id === id);
  const totalCount = comments.length;

  return (
    <div className="comments-section">
      <div className="comments-header">
        <h3 className="comments-title">Discussion</h3>
        <span className="comments-count">{totalCount} comment{totalCount !== 1 ? 's' : ''}</span>
      </div>

      <form className="comment-form" onSubmit={submit}>
        <div className="comment-form-avatar" aria-hidden="true">
          {user.avatar || user.name?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="comment-form-right">
          <label htmlFor="comment-input-main" className="sr-only">Add a comment</label>
          <textarea
            id="comment-input-main"
            className="comment-input"
            placeholder="Share a thought, ask a question, or leave feedback"
            value={text}
            onChange={e => setText(e.target.value)}
            rows={2}
            maxLength={1000}
          />
          <div className="comment-form-actions">
            <span className="comment-char">{text.length}/1000 characters</span>
            <button type="submit" className="btn btn-primary comment-submit" disabled={!text.trim() || posting}>
              {posting ? 'Posting…' : 'Post comment'}
            </button>
          </div>
          <p className={failed ? 'comment-error' : 'comment-status'} role={failed ? 'alert' : 'status'}>{notice}</p>
        </div>
      </form>

      {topLevel.length === 0 ? (
        <div className="comments-empty">No comments yet. Be the first to share your thoughts.</div>
      ) : (
        <div className="comments-list">
          {topLevel.map(c => (
            <CommentItem
              key={c.id}
              comment={c}
              replies={repliesFor(c.id)}
              user={user}
              onDelete={remove}
              onReply={addReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}
